import { useQuery } from "@tanstack/react-query";
import { fetchTransactions } from "@/services/api";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";

export const useTransactions = (initialLimit: number = 20) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTransactionId = searchParams.get("transactionId") || undefined;
  const initialInvoiceNumber = searchParams.get("invoiceNumber") || undefined;
  const initialProjectId = searchParams.get("projectId") || undefined;

  const [pagination, setPagination] = useState({
    limit: initialLimit,
    offset: 0,
    page: 1,
  });

  const [transactionIdFilter, setTransactionIdFilter] = useState<
    string | undefined
  >(initialTransactionId);
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState<
    string | undefined
  >(initialInvoiceNumber);
  const [projectIdFilter, setProjectIdFilter] = useState<string | undefined>(
    initialProjectId,
  );

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);

    if (transactionIdFilter) {
      newParams.set("transactionId", transactionIdFilter);
    } else {
      newParams.delete("transactionId");
    }

    if (invoiceNumberFilter) {
      newParams.set("invoiceNumber", invoiceNumberFilter);
    } else {
      newParams.delete("invoiceNumber");
    }

    if (projectIdFilter && projectIdFilter !== "all") {
      newParams.set("projectId", projectIdFilter);
    } else {
      newParams.delete("projectId");
    }

    setSearchParams(newParams);
  }, [
    transactionIdFilter,
    invoiceNumberFilter,
    projectIdFilter,
    setSearchParams,
    searchParams,
  ]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "transactions",
      pagination.limit,
      pagination.offset,
      transactionIdFilter,
      invoiceNumberFilter,
      projectIdFilter,
    ],
    queryFn: () =>
      fetchTransactions(
        pagination.limit,
        pagination.offset,
        transactionIdFilter,
        invoiceNumberFilter,
        projectIdFilter === "all" ? undefined : projectIdFilter,
      ),
    meta: {
      onError: (_err: Error) => {
        toast.error("Failed to load transactions", {
          description: "Please try again later or contact support.",
        });
      },
    },
  });

  const goToPage = (page: number) => {
    const newOffset = (page - 1) * pagination.limit;
    setPagination({
      ...pagination,
      offset: newOffset,
      page,
    });
  };

  const nextPage = () => {
    if (data && data.meta && pagination.page < data.meta.pages) {
      goToPage(pagination.page + 1);
    }
  };

  const prevPage = () => {
    if (pagination.page > 1) {
      goToPage(pagination.page - 1);
    }
  };

  const filterByTransactionId = (id: string | undefined) => {
    setTransactionIdFilter(id);
    // Reset to first page when filtering
    setPagination({
      ...pagination,
      offset: 0,
      page: 1,
    });
  };

  const filterByInvoiceNumber = (invoiceNumber: string | undefined) => {
    setInvoiceNumberFilter(invoiceNumber);
    // Reset to first page when filtering
    setPagination({
      ...pagination,
      offset: 0,
      page: 1,
    });
  };

  const filterByProjectId = (projectId: string | undefined) => {
    setProjectIdFilter(projectId);
    // Reset to first page when filtering
    setPagination({
      ...pagination,
      offset: 0,
      page: 1,
    });
  };

  const clearFilters = () => {
    setTransactionIdFilter(undefined);
    setInvoiceNumberFilter(undefined);
    setProjectIdFilter(undefined);
  };

  return {
    transactions: data?.data || [],
    meta: data?.meta,
    isLoading,
    isError,
    error,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    refetch,
    filterByTransactionId,
    filterByInvoiceNumber,
    filterByProjectId,
    clearFilters,
    transactionIdFilter,
    invoiceNumberFilter,
    projectIdFilter,
  };
};
